import { Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "./store/authStore";
import Layout from "./components/Layout";
import AdminLayout from "./components/AdminLayout";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import SurveysPage from "./pages/SurveysPage";
import SurveyDetailPage from "./pages/SurveyDetailPage";
import SurveyCreatePage from "./pages/SurveyCreatePage";
import SurveyEditPage from "./pages/SurveyEditPage";
import SurveyAnalyticsPage from "./pages/SurveyAnalyticsPage";
import ResponseSubmitPage from "./pages/ResponseSubmitPage";
import ResponseCompletePage from "./pages/ResponseCompletePage";
import WalletPage from "./pages/WalletPage";
import ProfilePage from "./pages/ProfilePage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminSurveysPage from "./pages/AdminSurveysPage";
import AdminTransactionsPage from "./pages/AdminTransactionsPage";
import AdminFeesPage from "./pages/AdminFeesPage";
import ResponderJoinPage from "./pages/ResponderJoinPage";
import ResponderDashboardPage from "./pages/ResponderDashboardPage";

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

// Admin-only route wrapper
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

// Smart dashboard: responders get Tinder-style UI, others get standard dashboard
function SmartDashboard() {
  const { user } = useAuthStore();
  if (user?.role === "responder") {
    return <ResponderDashboardPage />;
  }
  return <DashboardPage />;
}

function App() {
  const { isAuthenticated } = useAuthStore();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/join" element={!isAuthenticated ? <ResponderJoinPage /> : <Navigate to="/dashboard" replace />} />

      {/* Auth routes - redirect to dashboard if already authenticated */}
      <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <Navigate to="/dashboard" replace />} />
      <Route path="/register" element={!isAuthenticated ? <RegisterPage /> : <Navigate to="/dashboard" replace />} />

      {/* Protected routes with layout */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<SmartDashboard />} />
        <Route path="/surveys" element={<SurveysPage />} />
        <Route path="/surveys/create" element={<SurveyCreatePage />} />
        <Route path="/surveys/:id/edit" element={<SurveyEditPage />} />
        <Route path="/surveys/:id" element={<SurveyDetailPage />} />
        <Route path="/surveys/:id/analytics" element={<SurveyAnalyticsPage />} />
        <Route path="/surveys/:id/respond" element={<ResponseSubmitPage />} />
        <Route path="/surveys/:id/complete" element={<ResponseCompletePage />} />
        <Route path="/wallet" element={<WalletPage />} />
        <Route path="/profile" element={<ProfilePage />} />
      </Route>

      {/* Admin routes with admin layout */}
      <Route
        element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }
      >
        <Route path="/admin" element={<AdminDashboardPage />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/surveys" element={<AdminSurveysPage />} />
        <Route path="/admin/transactions" element={<AdminTransactionsPage />} />
        <Route path="/admin/fees" element={<AdminFeesPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
